import {useMemo, useCallback} from 'react'
import {useFieldArray, useForm} from 'react-hook-form'

import {useRefraModal} from '@refra/modules/common/providers'
import {useEditKilnReplacement} from '@refra/modules/refra-planning/hooks/mutations'
import {useRefraPlanningTracking} from '@refra/modules/refra-planning/hooks/tracking'
import {
  DrillingPosition,
  EditShutdownDrillingDTO,
  EditShutdownDrillingForm,
  KilnShutdown,
  ShutdownDrillingDTO
} from '@refra/modules/refra-planning/types'

interface UseUpdateMaterialInstallationFormParams {
  kilnLength: number
  drillingPositions: DrillingPosition[]
  shutdown: KilnShutdown
  drillings: ShutdownDrillingDTO[]
}

interface DrillingForm {
  drilling: {positions: EditShutdownDrillingForm[]}[]
}

export const useUpdateDrillingForm = ({
  kilnLength,
  drillingPositions,
  shutdown,
  drillings
}: UseUpdateMaterialInstallationFormParams) => {
  const {closeModal} = useRefraModal()
  const tracking = useRefraPlanningTracking()

  const {mutate: editKilnReplacement, isPending: isEditingDrilling} = useEditKilnReplacement()

  const defaultValues = useMemo<DrillingForm>(
    () => ({
      drilling: Array.from({length: kilnLength}, (_, index) => {
        const kilnMeter = index + 1
        return {
          positions: drillingPositions.map((position) => {
            const existingDrillingPosition = drillings?.find(
              (d) => position.id === d.drillingPosition?.id && d.meter === kilnMeter
            )
            return {
              meter: kilnMeter,
              drillingPositionId: position.id,
              isEnabled: position.isEnabled,
              depth: existingDrillingPosition?.depth,
              id: existingDrillingPosition?.id
            }
          })
        }
      })
    }),
    [kilnLength, drillingPositions, drillings]
  )

  const formMethods = useForm<DrillingForm>({
    defaultValues
  })

  const {control} = formMethods

  const {fields} = useFieldArray<DrillingForm, 'drilling'>({
    control,
    name: 'drilling'
  })

  const onSubmit = formMethods.handleSubmit((values: DrillingForm) => {
    // HCP-100840 add check if data is changed
    const drilling: EditShutdownDrillingDTO[] = []
    const existingDrillingIds = new Set(drillings.map((d) => d.id))
    let addedCount = 0
    let editedCount = 0
    let deletedCount = 0

    for (const drillingRow of values.drilling) {
      for (const position of drillingRow.positions) {
        if (position.depth !== undefined && position.depth !== null) {
          drilling.push({
            meter: position.meter,
            drillingPositionId: position.drillingPositionId,
            id: position.id,
            depth: position.depth
          })

          // Track if this is a new drilling or an edit
          if (position.id) {
            editedCount++
          } else {
            addedCount++
          }
        }
      }
    }

    // Count deleted drillings (existed before but not in new data)
    const newDrillingIds = new Set(drilling.map((d) => d.id).filter((id) => id !== undefined))
    deletedCount = Array.from(existingDrillingIds).filter((id) => !newDrillingIds.has(id)).length

    editKilnReplacement(
      {
        plantUpmId: shutdown.plantUpmId,
        kilnUpmId: shutdown.kilnUpmId,
        shutdownId: shutdown.id,
        kilnReplacementDTO: {
          drillings: drilling
        }
      },
      {
        onSuccess: () => {
          // Track drilling events based on what changed
          if (addedCount > 0) {
            tracking.drilling.add(shutdown.id)
          }
          if (editedCount > 0) {
            tracking.drilling.edit(shutdown.id)
          }
          if (deletedCount > 0) {
            tracking.drilling.remove(shutdown.id)
          }
          onClose()
        }
      }
    )
  })

  const onClose = useCallback(() => {
    formMethods.reset(defaultValues)
    closeModal()
  }, [closeModal, formMethods, defaultValues])

  return {formMethods, isFormBeingProcessed: isEditingDrilling, onSubmit, onClose, fields}
}
